import type { AppNavigateOptions } from "@/lib/use-app-navigate";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import type { GuideNextAction, GuideStepKey } from "./use-viewer-guide";

// Plain, functional copy; CON-432 owns the final wording.
export const GUIDE_STEP_LABELS: Record<GuideStepKey, string> = {
  create_project: "Create a project",
  join_project: "Get added to a project",
  upload_document: "Upload a schedule PDF",
  start_extraction: "Extract the schedule tables",
  review_tables: "Review the tables",
  download_export: "Download the submittal",
};

/** What the single primary button does. */
export type GuideCommand =
  | { kind: "navigate"; projectId: Id<"projects">; target: AppNavigateOptions }
  | { kind: "create_project" }
  | { kind: "set_up_billing" };

export interface GuideActionContent {
  title: string;
  description: string;
  actionLabel: string | null;
  command: GuideCommand | null;
}

function openJob(projectId: Id<"projects">, submittalId: Id<"specPilotSubmittals">): GuideCommand {
  return {
    kind: "navigate",
    projectId,
    target: { to: "/specPilot/$jobId", params: { jobId: submittalId }, title: "SpecPilot" },
  };
}

export function describeGuideAction(action: GuideNextAction, projectName: string | null): GuideActionContent {
  const inProject = projectName ? ` to ${projectName}` : "";
  switch (action.kind) {
    case "set_up_billing":
      return {
        title: "Set up your subscription",
        description: "Creating projects, uploading, and processing documents start once your company has an active plan.",
        actionLabel: "Set up billing",
        command: { kind: "set_up_billing" },
      };
    case "access_unavailable":
      return {
        title: "Editing is paused",
        description: "Your company can't create or process work right now. Ask your organization owner or admin about access.",
        actionLabel: null,
        command: null,
      };
    case "create_project":
      return {
        title: "Create your first project",
        description: "A project keeps a job's schedules, submittals, and team access together.",
        actionLabel: "Create project",
        command: { kind: "create_project" },
      };
    case "await_assignment":
      return {
        title: "Waiting for project access",
        description: "An admin needs to add you to a project. It will appear here as soon as they do.",
        actionLabel: null,
        command: null,
      };
    case "upload_document":
      return {
        title: "Upload a schedule PDF",
        description: `Add a door, hardware, or finish schedule${inProject}. SpecPilot reads its tables.`,
        actionLabel: "Open project",
        command: { kind: "navigate", projectId: action.projectId, target: { to: "/project", title: "Project" } },
      };
    case "start_extraction":
      return {
        title: "Extract the schedule tables",
        description: "Start a SpecPilot job from your uploaded schedule. New uploads are available once classification finishes.",
        actionLabel: "Start SpecPilot job",
        command: {
          kind: "navigate",
          projectId: action.projectId,
          target: { to: "/specPilot/new", title: "New SpecPilot Job" },
        },
      };
    case "await_processing":
      return {
        title: "SpecPilot is working",
        description: "SpecPilot is reading the schedule or building your submittal. You can leave this page; progress is saved.",
        actionLabel: "View job",
        command: openJob(action.projectId, action.submittalId),
      };
    case "retry_extraction":
      return {
        title: "Extraction didn't finish",
        description: "Open the job to see what happened, then try again.",
        actionLabel: "Open job",
        command: openJob(action.projectId, action.submittalId),
      };
    case "review_tables":
      return {
        title: "Review the extracted tables",
        description: "Check the tables, choose which belong in the submittal, then generate it.",
        actionLabel: "Review tables",
        command: openJob(action.projectId, action.submittalId),
      };
    case "await_export":
      return {
        title: "Waiting for a completed submittal",
        description: "Your team hasn't finished a submittal in your projects yet. You can open the project in the meantime.",
        actionLabel: "Open project",
        command: { kind: "navigate", projectId: action.projectId, target: { to: "/project", title: "Project" } },
      };
    case "download_export":
      return {
        title: "Download your submittal",
        description: "Your submittal is ready. Look it over, then download the CSV.",
        actionLabel: "Open submittal",
        command: {
          kind: "navigate",
          projectId: action.projectId,
          target: { to: "/specPilot/$jobId/submittal", params: { jobId: action.submittalId }, title: "Submittal" },
        },
      };
    case "done":
      return {
        title: "You're set up",
        description: "You've downloaded your first submittal.",
        actionLabel: null,
        command: null,
      };
  }
}
